<template>
  <div class="member-center">
    <!-- 页面标题区域 -->
    <section class="page-title">
      <div class="container">
        <h1>会员中心</h1>
        <p>欢迎回来，{{ userName }}</p>
      </div>
    </section>

    <!-- 会员仪表盘 -->
    <section class="dashboard">
      <div class="container">
        <div class="dashboard-grid">
          <div class="dashboard-card">
            <div class="card-icon">
              <i class="el-icon-s-data"></i>
            </div>
            <div class="card-content">
              <h3>我的积分</h3>
              <p class="card-value">{{ userPoints }}</p>
              <span class="card-desc">可用于兑换优惠券</span>
            </div>
          </div>
          
          <div class="dashboard-card">
            <div class="card-icon">
              <i class="el-icon-ticket"></i>
            </div>
            <div class="card-content">
              <h3>优惠券</h3>
              <p class="card-value">{{ userCoupons }}</p>
              <span class="card-desc">{{ userCouponsExpiring }}张即将到期</span>
            </div>
          </div>
          
          <div class="dashboard-card">
            <div class="card-icon">
              <i class="el-icon-star"></i>
            </div>
            <div class="card-content">
              <h3>会员等级</h3>
              <p class="card-value">{{ userLevel }}</p>
              <span class="card-desc">{{ levelProgress }}% 升至下一级</span>
            </div>
          </div>
          
          <div class="dashboard-card">
            <div class="card-icon">
              <i class="el-icon-message"></i>
            </div>
            <div class="card-content">
              <h3>消息通知</h3>
              <p class="card-value">{{ unreadMessages }}</p>
              <span class="card-desc">有新消息待查看</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 会员中心主内容区 -->
    <section class="main-content">
      <div class="container">
        <div class="content-layout">
          <!-- 左侧导航 -->
          <div class="sidebar">
            <div class="user-info">
              <div class="user-avatar">
                <img :src="userAvatar" :alt="userName">
              </div>
              <div class="user-details">
                <h3>{{ userName }}</h3>
                <p>{{ userLevel }}</p>
              </div>
            </div>
            
            <nav class="member-nav">
              <ul>
                <li 
                  v-for="(menu, index) in navMenus" 
                  :key="index"
                  :class="{ active: currentMenu === menu.key }"
                  @click="switchMenu(menu.key)"
                >
                  <i :class="menu.icon"></i>
                  <span>{{ menu.title }}</span>
                </li>
              </ul>
            </nav>
            
            <div class="member-badge">
              <div class="badge-icon">
                <i class="el-icon-crown"></i>
              </div>
              <div class="badge-info">
                <h4>VIP专享</h4>
                <p>尊享更多权益</p>
              </div>
              <el-button type="primary" size="mini">立即开通</el-button>
            </div>
          </div>
          
          <!-- 右侧内容 -->
          <div class="content-area">
            <!-- 个人资料 -->
            <div v-show="currentMenu === 'profile'" class="profile-section">
              <div class="section-header">
                <h2>个人资料</h2>
                <el-button @click="editProfile">编辑资料</el-button>
              </div>
              
              <div class="profile-form">
                <el-form :model="profileForm" label-width="120px">
                  <el-form-item label="用户名">
                    <el-input v-model="profileForm.username" disabled></el-input>
                  </el-form-item>
                  
                  <el-form-item label="昵称">
                    <el-input v-model="profileForm.nickname" :disabled="!isEditing"></el-input>
                  </el-form-item>
                  
                  <el-form-item label="手机号码">
                    <el-input v-model="profileForm.phone" disabled></el-input>
                  </el-form-item>
                  
                  <el-form-item label="邮箱">
                    <el-input v-model="profileForm.email" :disabled="!isEditing"></el-input>
                  </el-form-item>
                  
                  <el-form-item label="性别">
                    <el-radio-group v-model="profileForm.gender" :disabled="!isEditing">
                      <el-radio label="male">男</el-radio>
                      <el-radio label="female">女</el-radio>
                      <el-radio label="other">保密</el-radio>
                    </el-radio-group>
                  </el-form-item>
                  
                  <el-form-item label="生日">
                    <el-date-picker 
                      v-model="profileForm.birthday" 
                      type="date" 
                      :disabled="!isEditing"
                      value-format="YYYY-MM-DD"
                      placeholder="选择日期">
                    </el-date-picker>
                  </el-form-item>
                  
                  <el-form-item label="所在地区">
                    <el-cascader 
                      v-model="profileForm.address" 
                      :options="regionOptions" 
                      :disabled="!isEditing"
                      placeholder="选择地区">
                    </el-cascader>
                  </el-form-item>
                  
                  <el-form-item label="个人简介">
                    <el-input 
                      v-model="profileForm.bio" 
                      type="textarea" 
                      :disabled="!isEditing"
                      :rows="3">
                    </el-input>
                  </el-form-item>
                  
                  <el-form-item v-if="isEditing">
                    <el-button type="primary" @click="saveProfile">保存</el-button>
                    <el-button @click="cancelEdit">取消</el-button>
                  </el-form-item>
                </el-form>
              </div>
            </div>
            
            <!-- 我的订单 -->
            <div v-show="currentMenu === 'orders'" class="orders-section">
              <div class="section-header">
                <h2>我的订单</h2>
                <el-input 
                  v-model="orderSearch" 
                  placeholder="搜索订单号" 
                  prefix-icon="el-icon-search" 
                  clearable>
                </el-input>
              </div>
              
              <div class="order-tabs">
                <el-tabs v-model="activeOrderTab" type="card">
                  <el-tab-pane label="全部订单" name="all"></el-tab-pane>
                  <el-tab-pane label="待付款" name="pending"></el-tab-pane>
                  <el-tab-pane label="待发货" name="processing"></el-tab-pane>
                  <el-tab-pane label="待收货" name="shipping"></el-tab-pane>
                  <el-tab-pane label="待评价" name="received"></el-tab-pane>
                  <el-tab-pane label="已完成" name="completed"></el-tab-pane>
                </el-tabs>
              </div>
              
              <div class="order-list">
                <div 
                  class="order-item" 
                  v-for="(order, index) in filteredOrders" 
                  :key="index"
                >
                  <div class="order-header">
                    <span class="order-time">{{ order.createTime }}</span>
                    <span class="order-status" :class="`status-${order.status}`">{{ order.statusText }}</span>
                  </div>
                  
                  <div class="order-content">
                    <div class="order-products">
                      <div class="product-item" v-for="(product, idx) in order.products" :key="idx">
                        <div class="product-image">
                          <img :src="product.image" :alt="product.name">
                        </div>
                        <div class="product-info">
                          <h4 class="product-name">{{ product.name }}</h4>
                          <p class="product-spec">{{ product.spec }}</p>
                        </div>
                        <div class="product-price">
                          <span>¥{{ product.price }}</span>
                        </div>
                        <div class="product-quantity">
                          <span>x{{ product.quantity }}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div class="order-footer">
                      <div class="order-total">
                        <span>共{{ order.totalQuantity }}件商品，合计：</span>
                        <span class="total-price">¥{{ order.totalPrice }}</span>
                      </div>
                      
                      <div class="order-actions">
                        <el-button 
                          v-for="(action, actIdx) in order.actions" 
                          :key="actIdx"
                          :type="action.type || 'default'"
                          size="mini"
                          @click="handleOrderAction(order.id, action.action)"
                        >
                          {{ action.text }}
                        </el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 订单分页 -->
              <div class="order-pagination">
                <el-pagination
                  layout="prev, pager, next"
                  :total="filteredOrders.length"
                  :page-size="5"
                  @current-change="handleOrderPageChange"
                ></el-pagination>
              </div>
            </div>
            
            <!-- 我的优惠券 -->
            <div v-show="currentMenu === 'coupons'" class="coupons-section">
              <div class="section-header">
                <h2>我的优惠券</h2>
              </div>
              
              <div class="coupon-tabs">
                <el-tabs v-model="activeCouponTab" type="card">
                  <el-tab-pane label="未使用" name="unused"></el-tab-pane>
                  <el-tab-pane label="已使用" name="used"></el-tab-pane>
                  <el-tab-pane label="已过期" name="expired"></el-tab-pane>
                </el-tabs>
              </div>
              
              <div class="coupon-list">
                <div 
                  class="coupon-item" 
                  v-for="(coupon, index) in filteredCoupons" 
                  :key="index"
                  :class="{ used: coupon.status === 'used', expired: coupon.status === 'expired' }"
                >
                  <div class="coupon-left">
                    <div class="coupon-amount">
                      <span class="amount-symbol">¥</span>
                      <span class="amount-value">{{ coupon.amount }}</span>
                    </div>
                    <div class="coupon-min">满{{ coupon.minSpend }}元可用</div>
                  </div>
                  
                  <div class="coupon-right">
                    <h4 class="coupon-name">{{ coupon.name }}</h4>
                    <p class="coupon-scope">{{ coupon.scope }}</p>
                    <p class="coupon-time">有效期至：{{ coupon.expireDate }}</p>
                    <el-button 
                      v-if="coupon.status === 'unused'" 
                      type="primary" 
                      size="mini"
                      @click="useCoupon(coupon.id)"
                    >
                      立即使用
                    </el-button>
                    <span v-else-if="coupon.status === 'used'" class="coupon-status">已使用</span>
                    <span v-else-if="coupon.status === 'expired'" class="coupon-status">已过期</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 我的收藏 -->
            <div v-show="currentMenu === 'favorites'" class="favorites-section">
              <div class="section-header">
                <h2>我的收藏</h2>
              </div>
              
              <div class="favorites-grid">
                <div 
                  class="favorite-item" 
                  v-for="(favorite, index) in favorites" 
                  :key="index"
                >
                  <div class="favorite-image">
                    <img :src="favorite.image" :alt="favorite.name">
                    <button class="remove-btn" @click="removeFavorite(favorite.id)">
                      <i class="el-icon-delete"></i>
                    </button>
                  </div>
                  <h4 class="favorite-name">{{ favorite.name }}</h4>
                  <p class="favorite-price">¥{{ favorite.price }}</p>
                  <el-button type="primary" size="small" @click="buyNow(favorite)">立即购买</el-button>
                </div>
              </div>
              
              <!-- 收藏分页 -->
              <div class="favorites-pagination">
                <el-pagination
                  layout="prev, pager, next"
                  :total="favorites.length"
                  :page-size="6"
                  @current-change="handleFavoritesPageChange"
                ></el-pagination>
              </div>
            </div>
            
            <!-- 安全设置 -->
            <div v-show="currentMenu === 'security'" class="security-section">
              <div class="section-header">
                <h2>安全设置</h2>
              </div>
              
              <div class="security-list">
                <div class="security-item">
                  <div class="security-label">
                    <i class="el-icon-lock"></i>
                    <span>修改密码</span>
                  </div>
                  <div class="security-status">
                    <span>已设置</span>
                  </div>
                  <el-button @click="changePassword">修改</el-button>
                </div>
                
                <div class="security-item">
                  <div class="security-label">
                    <i class="el-icon-mobile"></i>
                    <span>手机绑定</span>
                  </div>
                  <div class="security-status">
                    <span>{{ userPhone }}</span>
                  </div>
                  <el-button @click="changePhone">更换</el-button>
                </div>
                
                <div class="security-item">
                  <div class="security-label">
                    <i class="el-icon-message"></i>
                    <span>邮箱绑定</span>
                  </div>
                  <div class="security-status">
                    <span>{{ userEmail }}</span>
                  </div>
                  <el-button @click="changeEmail">更换</el-button>
                </div>
                
                <div class="security-item">
                  <div class="security-label">
                    <i class="el-icon-shield"></i>
                    <span>实名认证</span>
                  </div>
                  <div class="security-status">
                    <span :class="{ verified: isVerified }">
                      {{ isVerified ? '已认证' : '未认证' }}
                    </span>
                  </div>
                  <el-button @click="verifyIdentity">
                    {{ isVerified ? '查看' : '去认证' }}
                  </el-button>
                </div>
                
                <div class="security-item">
                  <div class="security-label">
                    <i class="el-icon-setting"></i>
                    <span>登录设备管理</span>
                  </div>
                  <div class="security-status">
                    <span>{{ activeDevices }}个设备在线</span>
                  </div>
                  <el-button @click="manageDevices">管理</el-button>
                </div>
              </div>
            </div>
            
            <!-- 意见反馈 -->
            <div v-show="currentMenu === 'feedback'" class="feedback-section">
              <div class="section-header">
                <h2>意见反馈</h2>
              </div>
              
              <el-form :model="feedbackForm" label-width="100px">
                <el-form-item label="反馈类型">
                  <el-select v-model="feedbackForm.type" placeholder="选择反馈类型">
                    <el-option label="功能建议" value="suggestion"></el-option>
                    <el-option label="bug反馈" value="bug"></el-option>
                    <el-option label="服务投诉" value="complaint"></el-option>
                    <el-option label="其他" value="other"></el-option>
                  </el-select>
                </el-form-item>
                
                <el-form-item label="反馈内容">
                  <el-input 
                    v-model="feedbackForm.content" 
                    type="textarea" 
                    :rows="5"
                    placeholder="请详细描述您的问题或建议">
                  </el-input>
                </el-form-item>
                
                <el-form-item label="上传图片">
                  <el-upload
                    action="#"
                    list-type="picture-card"
                    :on-preview="handlePreview"
                    :on-remove="handleRemove"
                    :auto-upload="false"
                  >
                    <i class="el-icon-plus"></i>
                  </el-upload>
                </el-form-item>
                
                <el-form-item label="联系方式">
                  <el-input v-model="feedbackForm.contact" placeholder="请留下您的邮箱或手机号，方便我们联系您"></el-input>
                </el-form-item>
                
                <el-form-item>
                  <el-button type="primary" @click="submitFeedback">提交反馈</el-button>
                  <el-button @click="resetFeedback">重置</el-button>
                </el-form-item>
              </el-form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "MemberCenter",
  components: {},
  data() {
    return {
      // 用户基本信息
      userName: "张小明",
      userPoints: 2800,
      userCoupons: 12,
      userCouponsExpiring: 3,
      userLevel: "黄金会员",
      levelProgress: 75,
      unreadMessages: 5,
      userAvatar: "https://picsum.photos/id/1012/100/100",
      userPhone: "138****6789",
      userEmail: "zhang***@example.com",
      isVerified: true,
      activeDevices: 2,
      
      // 导航菜单
      currentMenu: 'profile',
      navMenus: [
        { key: 'profile', title: '个人资料', icon: 'el-icon-user' },
        { key: 'orders', title: '我的订单', icon: 'el-icon-document' },
        { key: 'coupons', title: '我的优惠券', icon: 'el-icon-ticket' },
        { key: 'favorites', title: '我的收藏', icon: 'el-icon-star-on' },
        { key: 'security', title: '安全设置', icon: 'el-icon-shield' },
        { key: 'feedback', title: '意见反馈', icon: 'el-icon-chat-dot-round' }
      ],
      
      // 个人资料表单
      isEditing: false,
      profileForm: {
        username: 'zhangxiaoming',
        nickname: '张小明',
        phone: '13812346789',
        email: 'zhangxiaoming@example.com',
        gender: 'male',
        birthday: '1990-01-15',
        address: ['广东省', '深圳市', '南山区'],
        bio: '热爱技术，喜欢创新'
      },
      originalForm: {},
      
      // 地区选项
      regionOptions: [
        {
          value: '广东省',
          label: '广东省',
          children: [
            {
              value: '深圳市',
              label: '深圳市',
              children: [
                { value: '南山区', label: '南山区' },
                { value: '福田区', label: '福田区' },
                { value: '罗湖区', label: '罗湖区' }
              ]
            },
            {
              value: '广州市',
              label: '广州市',
              children: [
                { value: '天河区', label: '天河区' },
                { value: '越秀区', label: '越秀区' }
              ]
            }
          ]
        }
      ],
      
      // 订单相关
      orderSearch: '',
      activeOrderTab: 'all',
      currentOrderPage: 1,
      orders: [
        {
          id: '20231120001',
          createTime: '2023-11-20 14:30:25',
          status: 'pending',
          statusText: '待付款',
          totalQuantity: 2,
          totalPrice: 398.00,
          products: [
            {
              id: 'p001',
              name: '智能办公软件高级版',
              spec: '企业版',
              price: 199.00,
              quantity: 2,
              image: 'https://picsum.photos/id/20/100/100'
            }
          ],
          actions: [
            { action: 'pay', text: '立即付款' },
            { action: 'cancel', text: '取消订单', type: 'text' }
          ]
        },
        {
          id: '20231115002',
          createTime: '2023-11-15 09:15:36',
          status: 'processing',
          statusText: '待发货',
          totalQuantity: 1,
          totalPrice: 599.00,
          products: [
            {
              id: 'p002',
              name: '数据安全防护系统',
              spec: '专业版',
              price: 599.00,
              quantity: 1,
              image: 'https://picsum.photos/id/26/100/100'
            }
          ],
          actions: [
            { action: 'contact', text: '联系客服', type: 'text' }
          ]
        },
        {
          id: '20231110003',
          createTime: '2023-11-10 16:45:12',
          status: 'shipping',
          statusText: '待收货',
          totalQuantity: 3,
          totalPrice: 897.00,
          products: [
            {
              id: 'p003',
              name: '云计算服务套餐',
              spec: '标准版',
              price: 299.00,
              quantity: 3,
              image: 'https://picsum.photos/id/29/100/100'
            }
          ],
          actions: [
            { action: 'track', text: '查看物流' },
            { action: 'confirm', text: '确认收货', type: 'primary' }
          ]
        },
        {
          id: '20231105004',
          createTime: '2023-11-05 11:20:58',
          status: 'received',
          statusText: '待评价',
          totalQuantity: 1,
          totalPrice: 1299.00,
          products: [
            {
              id: 'p004',
              name: '企业级数据分析平台',
              spec: '高级版',
              price: 1299.00,
              quantity: 1,
              image: 'https://picsum.photos/id/180/100/100'
            }
          ],
          actions: [
            { action: 'review', text: '评价商品', type: 'primary' },
            { action: 'rebuy', text: '再次购买' }
          ]
        },
        {
          id: '20231028005',
          createTime: '2023-10-28 15:30:42',
          status: 'completed',
          statusText: '已完成',
          totalQuantity: 2,
          totalPrice: 598.00,
          products: [
            {
              id: 'p005',
              name: '协同办公系统',
              spec: '团队版',
              price: 299.00,
              quantity: 2,
              image: 'https://picsum.photos/id/119/100/100'
            }
          ],
          actions: [
            { action: 'rebuy', text: '再次购买' },
            { action: 'detail', text: '订单详情', type: 'text' }
          ]
        }
      ],
      
      // 优惠券相关
      activeCouponTab: 'unused',
      coupons: [
        {
          id: 'c001',
          name: '新人专享优惠券',
          amount: 50,
          minSpend: 200,
          scope: '全场通用',
          expireDate: '2023-12-31',
          status: 'unused'
        },
        {
          id: 'c002',
          name: '企业服务满减券',
          amount: 100,
          minSpend: 500,
          scope: '企业服务类',
          expireDate: '2023-12-15',
          status: 'unused'
        },
        {
          id: 'c003',
          name: '技术支持优惠券',
          amount: 30,
          minSpend: 100,
          scope: '技术支持服务',
          expireDate: '2023-11-30',
          status: 'unused'
        },
        {
          id: 'c004',
          name: '会员日专享券',
          amount: 20,
          minSpend: 100,
          scope: '全场通用',
          expireDate: '2023-11-10',
          status: 'used'
        },
        {
          id: 'c005',
          name: '节日特惠券',
          amount: 100,
          minSpend: 300,
          scope: '全场通用',
          expireDate: '2023-10-31',
          status: 'expired'
        }
      ],
      
      // 收藏相关
      currentFavoritesPage: 1,
      favorites: [
        {
          id: 'f001',
          name: '企业数字化转型咨询服务',
          price: 3999.00,
          image: 'https://picsum.photos/id/24/200/200'
        },
        {
          id: 'f002',
          name: '云服务器年度套餐',
          price: 2999.00,
          image: 'https://picsum.photos/id/48/200/200'
        },
        {
          id: 'f003',
          name: '数据安全审计服务',
          price: 5999.00,
          image: 'https://picsum.photos/id/160/200/200'
        },
        {
          id: 'f004',
          name: '企业网站建设服务',
          price: 4999.00,
          image: 'https://picsum.photos/id/175/200/200'
        },
        {
          id: 'f005',
          name: '网络安全培训课程',
          price: 1299.00,
          image: 'https://picsum.photos/id/180/200/200'
        },
        {
          id: 'f006',
          name: 'IT运维外包服务',
          price: 7999.00,
          image: 'https://picsum.photos/id/239/200/200'
        }
      ],
      
      // 反馈表单
      feedbackForm: {
        type: '',
        content: '',
        contact: ''
      }
    };
  },
  computed: {
    // 筛选后的订单列表
    filteredOrders() {
      let result = [...this.orders];
      
      // 根据搜索关键词筛选
      if (this.orderSearch) {
        result = result.filter(order => order.id.includes(this.orderSearch));
      }
      
      // 根据标签筛选
      if (this.activeOrderTab !== 'all') {
        result = result.filter(order => order.status === this.activeOrderTab);
      }
      
      return result;
    },
    
    // 筛选后的优惠券列表
    filteredCoupons() {
      return this.coupons.filter(coupon => coupon.status === this.activeCouponTab);
    }
  },
  watch: {},
  created() {
    // 保存原始表单数据，用于取消编辑时恢复
    this.originalForm = { ...this.profileForm };
  },
  methods: {
    // 切换导航菜单
    switchMenu(key) {
      this.currentMenu = key;
    },
    
    // 编辑个人资料
    editProfile() {
      this.isEditing = true;
    },
    
    // 保存个人资料
    saveProfile() {
      // 这里可以添加表单验证和提交逻辑
      this.isEditing = false;
      this.$message.success('资料保存成功');
    },
    
    // 取消编辑
    cancelEdit() {
      this.isEditing = false;
      this.profileForm = { ...this.originalForm };
    },
    
    // 处理订单分页
    handleOrderPageChange(page) {
      this.currentOrderPage = page;
    },
    
    // 处理订单操作
    handleOrderAction(orderId, action) {
      console.log(`订单${orderId}执行${action}操作`);
      // 这里可以添加对应的操作逻辑
      switch(action) {
        case 'pay':
          this.$message('跳转到支付页面');
          break;
        case 'cancel':
          this.$confirm('确定要取消此订单吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.$message.success('订单已取消');
          });
          break;
        case 'track':
          this.$message('查看物流信息');
          break;
        case 'confirm':
          this.$confirm('确认已收到商品吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'info'
          }).then(() => {
            this.$message.success('收货成功');
          });
          break;
        case 'review':
          this.$message('跳转到评价页面');
          break;
        case 'rebuy':
          this.$message.success('已加入购物车');
          break;
      }
    },
    
    // 使用优惠券
    useCoupon(couponId) {
      console.log(`使用优惠券${couponId}`);
      this.$message('跳转到优惠券使用页面');
    },
    
    // 处理收藏分页
    handleFavoritesPageChange(page) {
      this.currentFavoritesPage = page;
    },
    
    // 移除收藏
    removeFavorite(favoriteId) {
      this.$confirm('确定要移除这个收藏吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.favorites.findIndex(item => item.id === favoriteId);
        if (index > -1) {
          this.favorites.splice(index, 1);
          this.$message.success('已移除收藏');
        }
      });
    },
    
    // 立即购买
    buyNow(product) {
      console.log(`购买商品: ${product.name}`);
      this.$message.success('已加入购物车');
    },
    
    // 修改密码
    changePassword() {
      this.$message('跳转到修改密码页面');
    },
    
    // 更换手机号
    changePhone() {
      this.$message('跳转到更换手机号页面');
    },
    
    // 更换邮箱
    changeEmail() {
      this.$message('跳转到更换邮箱页面');
    },
    
    // 实名认证
    verifyIdentity() {
      this.$message('跳转到实名认证页面');
    },
    
    // 管理登录设备
    manageDevices() {
      this.$message('跳转到设备管理页面');
    },
    
    // 处理上传图片预览
    handlePreview(file) {
      console.log('预览图片:', file);
    },
    
    // 处理移除上传图片
    handleRemove(file, fileList) {
      console.log('移除图片:', file);
    },
    
    // 提交反馈
    submitFeedback() {
      if (!this.feedbackForm.type) {
        this.$message.error('请选择反馈类型');
        return;
      }
      
      if (!this.feedbackForm.content) {
        this.$message.error('请填写反馈内容');
        return;
      }
      
      // 这里可以添加表单提交逻辑
      this.$message.success('反馈提交成功，感谢您的宝贵意见');
      this.resetFeedback();
    },
    
    // 重置反馈表单
    resetFeedback() {
      this.feedbackForm = {
        type: '',
        content: '',
        contact: ''
      };
    }
  }
};
</script>

<style scoped lang="less">
.container {
  width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 页面标题样式 */
.page-title {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;
  
  h1 {
    font-size: 42px;
    margin-bottom: 15px;
    font-weight: 600;
  }
  
  p {
    font-size: 18px;
    opacity: 0.9;
  }
}

/* 仪表盘样式 */
.dashboard {
  padding: 40px 0;
  background: #f5f7fa;
  
  .dashboard-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  
  .dashboard-card {
    background: #fff;
    border-radius: 12px;
    padding: 25px;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }
    
    .card-icon {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: #e6f7ff;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 20px;
      font-size: 24px;
      color: #1890ff;
    }
    
    .card-content {
      flex: 1;
      
      h3 {
        font-size: 16px;
        color: #666;
        margin-bottom: 8px;
        font-weight: normal;
      }
      
      .card-value {
        font-size: 28px;
        color: #333;
        font-weight: 600;
        margin-bottom: 5px;
      }
      
      .card-desc {
        font-size: 12px;
        color: #999;
      }
    }
  }
  
  /* 不同卡片的颜色主题 */
  .dashboard-card:nth-child(2) {
    .card-icon {
      background: #f6ffed;
      color: #52c41a;
    }
  }
  
  .dashboard-card:nth-child(3) {
    .card-icon {
      background: #fff7e6;
      color: #fa8c16;
    }
  }
  
  .dashboard-card:nth-child(4) {
    .card-icon {
      background: #fff0f6;
      color: #eb2f96;
    }
  }
}

/* 主内容区样式 */
.main-content {
  padding: 60px 0;
  
  .content-layout {
    display: flex;
    gap: 40px;
  }
  
  /* 左侧边栏样式 */
  .sidebar {
    flex: 0 0 280px;
    
    .user-info {
      background: #fff;
      border-radius: 12px;
      padding: 30px 20px;
      text-align: center;
      margin-bottom: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      
      .user-avatar {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        overflow: hidden;
        margin: 0 auto 15px;
        border: 3px solid #e6f7ff;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
      
      .user-details {
        h3 {
          font-size: 20px;
          color: #333;
          margin-bottom: 8px;
          font-weight: 600;
        }
        
        p {
          font-size: 14px;
          color: #1890ff;
          background: #e6f7ff;
          padding: 3px 12px;
          border-radius: 15px;
          display: inline-block;
        }
      }
    }
    
    .member-nav {
      background: #fff;
      border-radius: 12px;
      padding: 20px 0;
      margin-bottom: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
          padding: 15px 30px;
          cursor: pointer;
          display: flex;
          align-items: center;
          font-size: 16px;
          color: #666;
          transition: all 0.3s ease;
          
          &:hover {
            background: #f5f7fa;
            color: #1890ff;
          }
          
          &.active {
            background: #e6f7ff;
            color: #1890ff;
            
            i {
              color: #1890ff;
            }
          }
          
          i {
            margin-right: 15px;
            font-size: 18px;
            width: 20px;
            text-align: center;
          }
        }
      }
    }
    
    .member-badge {
      background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
      border-radius: 12px;
      padding: 25px 20px;
      color: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      
      .badge-icon {
        font-size: 36px;
        margin-bottom: 15px;
      }
      
      .badge-info {
        margin-bottom: 20px;
        
        h4 {
          font-size: 18px;
          margin-bottom: 5px;
          font-weight: 600;
        }
        
        p {
          font-size: 14px;
          opacity: 0.9;
        }
      }
      
      .el-button {
        width: 100%;
        background: #fff;
        color: #ff9800;
        border: none;
        
        &:hover {
          background: #f5f5f5;
          color: #ff9800;
        }
      }
    }
  }
  
  /* 右侧内容区样式 */
  .content-area {
    flex: 1;
    background: #fff;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      padding-bottom: 15px;
      border-bottom: 1px solid #f0f0f0;
      
      h2 {
        font-size: 24px;
        color: #333;
        margin: 0;
        font-weight: 600;
      }
    }
    
    /* 个人资料样式 */
    .profile-form {
      .el-form-item {
        margin-bottom: 25px;
      }
      
      .el-input,
      .el-select,
      .el-radio-group,
      .el-date-editor {
        width: 100%;
        max-width: 400px;
      }
    }
    
    /* 订单相关样式 */
    .order-tabs {
      margin-bottom: 30px;
      
      .el-tabs__header {
        margin-bottom: 20px;
      }
    }
    
    .order-list {
      margin-bottom: 30px;
      
      .order-item {
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        margin-bottom: 20px;
        overflow: hidden;
        
        .order-header {
          background: #fafafa;
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f0f0f0;
          
          .order-time {
            font-size: 14px;
            color: #666;
          }
          
          .order-status {
            font-size: 14px;
            font-weight: 500;
            
            &.status-pending {
              color: #faad14;
            }
            
            &.status-processing {
              color: #1890ff;
            }
            
            &.status-shipping {
              color: #52c41a;
            }
            
            &.status-received {
              color: #722ed1;
            }
            
            &.status-completed {
              color: #999;
            }
          }
        }
        
        .order-content {
          .order-products {
            padding: 20px;
            
            .product-item {
              display: flex;
              align-items: center;
              padding: 15px 0;
              border-bottom: 1px solid #f5f5f5;
              
              &:last-child {
                border-bottom: none;
              }
              
              .product-image {
                width: 80px;
                height: 80px;
                border-radius: 4px;
                overflow: hidden;
                margin-right: 15px;
                
                img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                }
              }
              
              .product-info {
                flex: 1;
                
                .product-name {
                  font-size: 16px;
                  color: #333;
                  margin-bottom: 8px;
                  font-weight: 500;
                }
                
                .product-spec {
                  font-size: 14px;
                  color: #999;
                }
              }
              
              .product-price {
                font-size: 16px;
                color: #ff4d4f;
                font-weight: 600;
                margin: 0 30px;
              }
              
              .product-quantity {
                font-size: 14px;
                color: #666;
              }
            }
          }
          
          .order-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            background: #fafafa;
            
            .order-total {
              font-size: 14px;
              color: #666;
              
              .total-price {
                font-size: 18px;
                color: #ff4d4f;
                font-weight: 600;
                margin-left: 5px;
              }
            }
            
            .order-actions {
              .el-button {
                margin-left: 10px;
              }
            }
          }
        }
      }
    }
    
    .order-pagination {
      text-align: center;
    }
    
    /* 优惠券样式 */
    .coupon-tabs {
      margin-bottom: 30px;
      
      .el-tabs__header {
        margin-bottom: 20px;
      }
    }
    
    .coupon-list {
      .coupon-item {
        display: flex;
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 20px;
        background: #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        position: relative;
        
        &.used,
        &.expired {
          opacity: 0.7;
        }
        
        &.used::after,
        &.expired::after {
          content: attr(data-status);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 36px;
          font-weight: bold;
          color: rgba(0, 0, 0, 0.1);
          z-index: 1;
        }
        
        &.used {
          &::after {
            content: '已使用';
          }
        }
        
        &.expired {
          &::after {
            content: '已过期';
          }
        }
        
        .coupon-left {
          flex: 0 0 180px;
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
          color: #fff;
          padding: 30px 20px;
          text-align: center;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: relative;
          
          &::after {
            content: '';
            position: absolute;
            right: -10px;
            top: 50%;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #f5f7fa;
          }
          
          &::before {
            content: '';
            position: absolute;
            right: -10px;
            bottom: -10px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #f5f7fa;
          }
          
          .coupon-amount {
            display: flex;
            align-items: baseline;
            justify-content: center;
            margin-bottom: 10px;
            
            .amount-symbol {
              font-size: 24px;
              font-weight: normal;
              margin-right: 5px;
            }
            
            .amount-value {
              font-size: 48px;
              font-weight: 600;
            }
          }
          
          .coupon-min {
            font-size: 14px;
            opacity: 0.9;
          }
        }
        
        .coupon-right {
          flex: 1;
          padding: 25px;
          display: flex;
          flex-direction: column;
          
          .coupon-name {
            font-size: 18px;
            color: #333;
            margin-bottom: 10px;
            font-weight: 600;
          }
          
          .coupon-scope {
            font-size: 14px;
            color: #666;
            margin-bottom: 8px;
          }
          
          .coupon-time {
            font-size: 12px;
            color: #999;
            margin-bottom: 20px;
          }
          
          .el-button {
            margin-top: auto;
            align-self: flex-start;
          }
          
          .coupon-status {
            display: inline-block;
            padding: 4px 12px;
            background: #f5f5f5;
            color: #999;
            border-radius: 15px;
            font-size: 12px;
            margin-top: auto;
            align-self: flex-start;
          }
        }
      }
    }
    
    /* 收藏样式 */
    .favorites-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      margin-bottom: 30px;
      
      .favorite-item {
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        overflow: hidden;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        position: relative;
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }
        
        .favorite-image {
          height: 160px;
          overflow: hidden;
          position: relative;
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
          }
          
          &:hover img {
            transform: scale(1.05);
          }
          
          .remove-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.5);
            color: #fff;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
            
            &:hover {
              background: rgba(0, 0, 0, 0.7);
            }
          }
          
          &:hover .remove-btn {
            opacity: 1;
          }
        }
        
        .favorite-name {
          padding: 15px;
          font-size: 16px;
          color: #333;
          margin: 0;
          font-weight: 500;
          min-height: 60px;
        }
        
        .favorite-price {
          padding: 0 15px 15px;
          font-size: 18px;
          color: #ff4d4f;
          font-weight: 600;
        }
        
        .el-button {
          width: calc(100% - 30px);
          margin: 0 15px 15px;
        }
      }
    }
    
    .favorites-pagination {
      text-align: center;
    }
    
    /* 安全设置样式 */
    .security-list {
      .security-item {
        display: flex;
        align-items: center;
        padding: 20px 0;
        border-bottom: 1px solid #f0f0f0;
        
        &:last-child {
          border-bottom: none;
        }
        
        .security-label {
          display: flex;
          align-items: center;
          flex: 0 0 200px;
          
          i {
            font-size: 20px;
            color: #1890ff;
            margin-right: 15px;
          }
          
          span {
            font-size: 16px;
            color: #333;
          }
        }
        
        .security-status {
          flex: 1;
          font-size: 14px;
          
          span {
            color: #666;
            
            &.verified {
              color: #52c41a;
            }
          }
        }
      }
    }
    
    /* 反馈样式 */
    .feedback-section {
      .el-form-item {
        margin-bottom: 25px;
      }
      
      .el-input,
      .el-select {
        width: 100%;
        max-width: 500px;
      }
      
      .el-upload {
        max-width: 500px;
      }
    }
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .container {
    width: 100%;
    padding: 0 30px;
  }
  
  .dashboard {
    .dashboard-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
  }
  
  .main-content {
    .content-layout {
      gap: 30px;
    }
    
    .sidebar {
      flex: 0 0 250px;
    }
    
    .content-area {
      padding: 25px;
    }
    
    .favorites-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .page-title {
    padding: 60px 0;
    
    h1 {
      font-size: 32px;
    }
    
    p {
      font-size: 16px;
    }
  }
  
  .dashboard {
    padding: 30px 0;
    
    .dashboard-grid {
      grid-template-columns: 1fr;
      gap: 15px;
    }
  }
  
  .main-content {
    padding: 40px 0;
    
    .content-layout {
      flex-direction: column;
      gap: 20px;
    }
    
    .sidebar {
      flex: none;
    }
    
    .content-area {
      padding: 20px;
      
      .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
        
        h2 {
          font-size: 20px;
        }
        
        .el-input {
          width: 100%;
        }
      }
      
      .profile-form {
        .el-input,
        .el-select,
        .el-date-editor {
          max-width: none;
        }
      }
      
      .order-content {
        .product-item {
          flex-wrap: wrap;
          
          .product-image {
            width: 60px;
            height: 60px;
          }
          
          .product-price {
            margin: 0 15px;
          }
        }
        
        .order-footer {
          flex-direction: column;
          align-items: flex-end;
          gap: 15px;
        }
      }
      
      .coupon-item {
        flex-direction: column;
        
        .coupon-left {
          flex: none;
          width: 100%;
          &::after,
          &::before {
            display: none;
          }
        }
      }
      
      .favorites-grid {
        grid-template-columns: 1fr;
      }
      
      .security-item {
        flex-wrap: wrap;
        
        .security-label {
          flex: 0 0 150px;
          
          i {
            font-size: 18px;
            margin-right: 10px;
          }
          
          span {
            font-size: 14px;
          }
        }
      }
      
      .feedback-section {
        .el-input,
        .el-select {
          max-width: none;
        }
      }
    }
  }
}
</style>